/**
 * Small HeroUI fields the Updates view and the per-machine update control share: a single-choice
 * Select over a short list of options, and a whole-number field with steppers.
 */

import { Label, ListBox, NumberField, Select } from '@heroui/react';
import type { Key, ReactNode } from 'react';

export interface Option<K extends string> {
  id: K;
  label: string;
  isDisabled?: boolean;
}

/** One choice from a short list. `label` is visible when given, else it is the accessible name only. */
export function OptionSelect<K extends string>({ options, value, onChange, ariaLabel, label, placeholder, isDisabled, className }: { options: Array<Option<K>>; value: K | null; onChange: (k: K) => void; ariaLabel: string; label?: ReactNode; placeholder?: string; isDisabled?: boolean; className?: string }) {
  return (
    <Select
      aria-label={label ? undefined : ariaLabel}
      className={className ?? 'w-full'}
      value={value}
      placeholder={placeholder}
      isDisabled={isDisabled}
      disabledKeys={options.filter((o) => o.isDisabled).map((o) => o.id)}
      onChange={(k: Key | null) => {
        if (k !== null) onChange(String(k) as K);
      }}
    >
      {label ? <Label>{label}</Label> : null}
      <Select.Trigger>
        <Select.Value />
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>
        <ListBox>
          {options.map((o) => (
            <ListBox.Item key={o.id} id={o.id} textValue={o.label}>
              {o.label}
              <ListBox.ItemIndicator />
            </ListBox.Item>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  );
}

/** A whole number between `min` and `max`, committed as it changes (steppers or typing then leaving the field). */
export function WholeNumber({ value, min, max, onChange, ariaLabel, isDisabled }: { value: number; min: number; max: number; onChange: (n: number) => void; ariaLabel: string; isDisabled?: boolean }) {
  return (
    <NumberField
      aria-label={ariaLabel}
      className="inline-flex w-auto"
      value={value}
      minValue={min}
      maxValue={max}
      step={1}
      formatOptions={{ maximumFractionDigits: 0 }}
      isDisabled={isDisabled}
      onChange={(n) => {
        if (Number.isInteger(n) && n >= min && n <= max && n !== value) onChange(n);
      }}
    >
      <NumberField.Group>
        <NumberField.DecrementButton />
        <NumberField.Input className="w-10 text-center tabular-nums" />
        <NumberField.IncrementButton />
      </NumberField.Group>
    </NumberField>
  );
}
